import { type WheelEvent } from 'react';

import type {
  DragEndProps,
  DragMoveProps,
  DragStartProps,
  PointerEventModifiers,
  PointerEventPosition,
} from '../../../types/pointer-events.js';
import {
  panDragEnd,
  panDragEndWithMiddleButton,
  panDragMove,
  panDragMoveWithMiddleButton,
  panDragStart,
  panDragStartWithMiddleButton,
} from '../interactions/pan/dragInteractions.js';
import {
  panKeyboardEscape,
  panKeyboardLeft,
  panKeyboardRight,
} from '../interactions/pan/keyboardInteractions.js';
import {
  panMouseClickOrTap,
  panMouseHover,
  panMouseWheel,
} from '../interactions/pan/mouseInteractions.js';
import {
  zoomKeyboardDown,
  zoomKeyboardUp,
} from '../interactions/zoom/keyboardInteractions.js';
import type {
  InteractionsHandlers,
  InteractionTriggers,
  panInteractions,
} from '../types.js';
import { reset } from './reset.js';

export const getPanInteractions = ({
  panDeltaRef,
  onPanStart,
  selectionArea,
  eventCancelled,
  onSelectEnd,
  lastMousePositionRef,
  pinned,
  onHover,
  onTogglePin,
  setCursorMode,
  height,
  onReset,
  width,
  onZoomChange,
  onModeChange,
  onPanEnd,
  onPanChange,
  cursorMode,
}: panInteractions): InteractionsHandlers => {
  return {
    onMouseHover: (event: PointerEventPosition & PointerEventModifiers) =>
      panMouseHover({
        event,
        pinned,
        lastMousePositionRef,
        cursorMode,
        setCursorMode,
        onHover,
      }),
    onMouseClickOrTap: (event: PointerEventPosition & PointerEventModifiers) =>
      panMouseClickOrTap({ event, lastMousePositionRef }),
    onDragStart: (event: DragStartProps & PointerEventModifiers) =>
      panDragStart({ panDeltaRef, onPanStart }),
    onDragMove: (event: DragMoveProps & PointerEventModifiers) =>
      panDragMove({ event, height, panDeltaRef, onPanChange }),
    onDragEnd: (event: DragEndProps & PointerEventModifiers) =>
      panDragEnd({ panDeltaRef, onPanEnd }),
    onDragStartWithMiddleButton: () =>
      panDragStartWithMiddleButton({ panDeltaRef, onPanStart }),
    onDragMoveWithMiddleButton: (
      event: DragMoveProps & PointerEventModifiers,
    ) => panDragMoveWithMiddleButton({ event, panDeltaRef, onPanChange }),
    onDragEndWithMiddleButton: (event: DragEndProps & PointerEventModifiers) =>
      panDragEndWithMiddleButton({
        event,
        panDeltaRef,
        lastMousePositionRef,
        onPanEnd,
      }),
    onDoubleClickOrTap: (triggeredBy: InteractionTriggers) => {
      onReset?.({
        triggeredBy,
      });

      reset(lastMousePositionRef, panDeltaRef);
    },
    onWheel: (event: WheelEvent) => panMouseWheel({ event, onPanChange }),
    onKeyEscape: () =>
      panKeyboardEscape({
        selectionArea,
        eventCancelled,
        onSelectEnd,
        lastMousePositionRef,
        pinned,
        onHover,
        onTogglePin,
        onModeChange,
        onPanEnd,
      }),
    onKeyEnter: () => {
      return;
    },
    onKeyRight: (shiftKey: boolean) =>
      panKeyboardRight({ shiftKey, width, onPanChange }),
    onKeyLeft: (shiftKey: boolean) =>
      panKeyboardLeft({ shiftKey, width, onPanChange }),
    onKeyUp: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardUp({ width, onZoomChange });
      }
    },
    onKeyDown: (isTemporaryZoomMode: boolean) => {
      if (isTemporaryZoomMode) {
        zoomKeyboardDown({ width, onZoomChange });
      }
    },
  };
};
